"use client";

import { useEffect, useRef } from "react";
import { useAuth } from "../../util/auth";
import {
  CONSENT_UPDATED_EVENT,
  clearUserIdentity,
  getMeasurementId,
  identifyUser,
  readStoredConsent,
} from "../../lib/analytics";
import { relayAnalyticsConsent } from "../../lib/analytics/consent-relay";
import { retrieveGaClientId } from "../../lib/google/ga-cookie";

interface AuthUserLike {
  uid?: string;
  customClaims?: { tier?: string } & Record<string, unknown>;
}

interface AuthContextLike {
  user?: AuthUserLike | false | null;
  getAccessToken?: () => Promise<string | null>;
}

const CLIENT_ID_RETRY_INTERVAL_MS = 500;
const CLIENT_ID_MAX_RETRY_ATTEMPTS = 20;
const RELAY_RETRY_INTERVAL_MS = 1000;
const RELAY_MAX_RETRY_ATTEMPTS = 10;

function isAuthenticatedUser(
  user: AuthUserLike | false | null | undefined
): user is AuthUserLike & { uid: string } {
  return Boolean(user && typeof user === "object" && typeof user.uid === "string");
}

/**
 * Pushes the authenticated Firebase uid into GA4 as `user_id` whenever the
 * auth state changes, and clears it on sign-out. After authentication it also
 * persists the stored consent decision to Composer even when `_ga` is not
 * ready, then synchronizes a late-arriving GA client ID without blocking
 * the auth flow.
 *
 * US visitors do not see a banner: consent is granted in bootstrap when
 * the edge country is known, or after a geo lookup. That grant can finish
 * after login, so this observer also listens for consent updates and
 * retries the same non-blocking relay.
 *
 * We intentionally send only the uid (never email) because Google's data
 * processing terms forbid sending PII to GA4.
 */
export default function UserIdentityTracker() {
  const measurementId = getMeasurementId();
  const authContext = useAuth() as AuthContextLike;
  const { user } = authContext;
  const lastUid = useRef<string | null>(null);
  const userRef = useRef(user);
  const getAccessTokenRef = useRef(authContext.getAccessToken);
  const lastSyncedClientId = useRef<string | null>(null);
  const syncingClientIds = useRef<Set<string>>(new Set());
  const lookupInFlightByUid = useRef<Set<string>>(new Set());
  const retryTimerByUid = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const relayRetryTimerByUid = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const relayRetryCountByUid = useRef<Map<string, number>>(new Map());
  const syncClientIdIfEligibleRef = useRef<
    ((_authUser: AuthUserLike & { uid: string }) => void) | null
  >(null);
  userRef.current = user;
  getAccessTokenRef.current = authContext.getAccessToken;

  const clearLookupState = (uid: string) => {
    const timer = retryTimerByUid.current.get(uid);
    if (timer) {
      clearTimeout(timer);
      retryTimerByUid.current.delete(uid);
    }
    lookupInFlightByUid.current.delete(uid);
  };

  const clearRelayRetryState = (uid: string) => {
    const timer = relayRetryTimerByUid.current.get(uid);
    if (timer) {
      clearTimeout(timer);
      relayRetryTimerByUid.current.delete(uid);
    }
    relayRetryCountByUid.current.delete(uid);
  };

  const scheduleRelayRetry = (authUser: AuthUserLike & { uid: string }) => {
    const current = relayRetryCountByUid.current.get(authUser.uid) ?? 0;
    if (current >= RELAY_MAX_RETRY_ATTEMPTS) {
      clearRelayRetryState(authUser.uid);
      return;
    }
    relayRetryCountByUid.current.set(authUser.uid, current + 1);
    const existingTimer = relayRetryTimerByUid.current.get(authUser.uid);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    const timer = setTimeout(() => {
      relayRetryTimerByUid.current.delete(authUser.uid);
      const latestUser = userRef.current;
      if (
        !latestUser ||
        typeof latestUser !== "object" ||
        latestUser.uid !== authUser.uid
      ) {
        clearRelayRetryState(authUser.uid);
        return;
      }
      syncClientIdIfEligibleRef.current?.(authUser);
    }, RELAY_RETRY_INTERVAL_MS);
    relayRetryTimerByUid.current.set(authUser.uid, timer);
  };

  const relayConsentDecision = (
    authUser: AuthUserLike & { uid: string },
    choice: "granted" | "denied",
    clientId?: string
  ) => {
    const syncKey = clientId
      ? `${authUser.uid}:${choice}:${clientId}`
      : `${authUser.uid}:${choice}:consent`;
    if (
      lastSyncedClientId.current === syncKey ||
      syncingClientIds.current.has(syncKey)
    ) {
      return;
    }
    syncingClientIds.current.add(syncKey);
    void (async () => {
      try {
        const accessToken = await getAccessTokenRef.current?.();
        if (!accessToken) {
          scheduleRelayRetry(authUser);
          return;
        }
        const synced = await relayAnalyticsConsent(
          choice,
          accessToken,
          clientId
        );
        if (synced) {
          lastSyncedClientId.current = syncKey;
          clearRelayRetryState(authUser.uid);
          return;
        }
        scheduleRelayRetry(authUser);
      } finally {
        syncingClientIds.current.delete(syncKey);
      }
    })();
  };

  const syncClientIdIfEligible = (authUser: AuthUserLike & { uid: string }) => {
    const consent = readStoredConsent();
    if (!consent) {
      clearLookupState(authUser.uid);
      return;
    }
    relayConsentDecision(authUser, consent.analytics_storage);
    if (consent.analytics_storage !== "granted") {
      clearLookupState(authUser.uid);
      return;
    }
    if (!measurementId) {
      clearLookupState(authUser.uid);
      return;
    }
    if (lookupInFlightByUid.current.has(authUser.uid)) return;
    lookupInFlightByUid.current.add(authUser.uid);

    const runAttempt = (attempt: number) => {
      let resolved = false;
      retrieveGaClientId(measurementId, (clientId) => {
        resolved = true;
        clearLookupState(authUser.uid);
        relayConsentDecision(authUser, "granted", clientId);
      });
      if (resolved) return;
      if (attempt >= CLIENT_ID_MAX_RETRY_ATTEMPTS) {
        clearLookupState(authUser.uid);
        return;
      }
      const timer = setTimeout(() => {
        runAttempt(attempt + 1);
      }, CLIENT_ID_RETRY_INTERVAL_MS);
      retryTimerByUid.current.set(authUser.uid, timer);
    };

    runAttempt(1);
  };
  syncClientIdIfEligibleRef.current = syncClientIdIfEligible;

  useEffect(() => {
    const authUser = user as AuthUserLike | false | null | undefined;
    if (isAuthenticatedUser(authUser)) {
      if (lastUid.current !== authUser.uid) {
        if (lastUid.current) {
          clearLookupState(lastUid.current);
          clearRelayRetryState(lastUid.current);
        }
        lastUid.current = authUser.uid;
        lastSyncedClientId.current = null;
        syncingClientIds.current.clear();
        if (measurementId) {
          const tier =
            authUser.customClaims && typeof authUser.customClaims.tier === "string"
              ? authUser.customClaims.tier
              : undefined;
          identifyUser(authUser.uid, {
            logged_in: "true",
            ...(tier ? { subscription_tier: tier } : {}),
          });
        }
      }
      syncClientIdIfEligible(authUser);
      return;
    }

    if (lastUid.current !== null) {
      clearLookupState(lastUid.current);
      clearRelayRetryState(lastUid.current);
      lastUid.current = null;
      lastSyncedClientId.current = null;
      syncingClientIds.current.clear();
      clearUserIdentity();
    }
  }, [measurementId, user]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onConsentUpdated = () => {
      const authUser = userRef.current;
      if (!isAuthenticatedUser(authUser)) return;
      syncClientIdIfEligibleRef.current?.(authUser);
    };
    window.addEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);
    return () => {
      window.removeEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);
    };
  }, [measurementId]);

  useEffect(() => {
    return () => {
      for (const timer of retryTimerByUid.current.values()) {
        clearTimeout(timer);
      }
      retryTimerByUid.current.clear();
      lookupInFlightByUid.current.clear();
      for (const timer of relayRetryTimerByUid.current.values()) {
        clearTimeout(timer);
      }
      relayRetryTimerByUid.current.clear();
      relayRetryCountByUid.current.clear();
    };
  }, []);

  return null;
}
